/**
 * serialDecoder.js — LegitGrails serial / date-code decoder engine
 *
 * ONE file. Shipped to Shopify as a theme asset and imported by `bun test`
 * BYTE-IDENTICAL. There is deliberately no build step and no compiled twin:
 * a TS source plus a generated copy always drifts, and drift here means the
 * tested logic is not the shipped logic.
 *
 * Vanilla ES2017. No dependencies. Narrative 9.0.8 already ships jQuery 3.7.1
 * and we add nothing to that.
 *
 * All brand knowledge lives in the rulesets (rules/<brand>.json). The engine
 * knows only how to execute a CLOSED SET of seven ops. Adding brand #13 must
 * require zero changes to this file — if a brand seems to need an eighth op,
 * that is a signal to re-examine the source, not to bolt on a special case.
 *
 * There is no `eval` and no code in JSON, by design: rulesets are data that
 * gets published to a public page, and executable JSON is an XSS vector.
 */
(function (root, factory) {
  "use strict";
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.SerialDecoder = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Result states. All eight are real outcomes and all eight get designed UI —
  // an unhandled state shows the user a blank box, which reads as "broken".
  // ---------------------------------------------------------------------------
  var STATE = {
    DECODED: "DECODED",                       // resolved to a single year/era
    AMBIGUOUS: "AMBIGUOUS",                   // >1 genuine candidate — see note below
    FORMAT_OK_NO_DATE: "FORMAT_OK_NO_DATE",   // valid format, brand encodes no date
    INVALID_FORMAT: "INVALID_FORMAT",         // matches no documented era's LAYOUT
    UNLISTED_VALUE: "UNLISTED_VALUE",         // layout matched, a value is not in our tables
    IMPOSSIBLE_DATE: "IMPOSSIBLE_DATE",       // reads as a date that cannot exist (yet)
    NO_CODE_ERA: "NO_CODE_ERA",               // brand stopped using codes
    OUT_OF_SCOPE: "OUT_OF_SCOPE"              // documented, but not this brand/line
  };

  /**
   * The three MISS states. Until round 3 INVALID_FORMAT meant two different
   * things — "that is not the shape this brand prints" and "the shape is right
   * but a value in it is not in our tables" — and the reader got the same
   * sentence for both. The second is far more common on a real item (an
   * unlisted LV factory, a misread digit) and deserves a softer answer, so it
   * is now its own state, and so is a code that reads as a date that has not
   * happened or cannot exist.
   *
   * Every consumer that used to test `=== INVALID_FORMAT` to mean "we could not
   * read it" must test isMiss() instead, or a new miss is counted as a lookup.
   */
  var MISS = {};
  MISS[STATE.INVALID_FORMAT] = true;
  MISS[STATE.UNLISTED_VALUE] = true;
  MISS[STATE.IMPOSSIBLE_DATE] = true;
  function isMiss(state) { return MISS[state] === true; }

  /**
   * AMBIGUOUS is the state competitors do not have, and it is not a failure
   * mode — it is the honest answer. A Hermès "Y" with no frame is 1969 OR 2020;
   * an LV "0" is 1990 OR 2000 OR 2010. Every competitor silently picks one.
   * We return every candidate plus the question that would separate them.
   */

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function isObj(v) { return v !== null && typeof v === "object" && !Array.isArray(v); }

  // ---------------------------------------------------------------------------
  // WIDTH AND LOOK-ALIKE FOLDING — the fix for a silent wrong answer.
  //
  // A ja/ko/zh visitor who has not switched their IME out of wide mode types
  // ４９３０７５, not 493075. `toUpperCase()` does not fold that, and every
  // ruleset's stripPattern is `[^A-Z0-9]`, so the whole code was DELETED and
  // the reader was told a perfectly good number was not recognised. NFKC folds
  // the full-width block, the ideographic space and the full-width hyphen and
  // colon back to ASCII, which is exactly the class of input at fault.
  //
  // ⚠️ NOTHING HERE MAPS ONE SCRIPT ONTO ANOTHER. NFKC is width- and
  // compatibility-only: it never turns Cyrillic А (U+0410) into Latin A, and
  // neither does the table below. If it did, a code typed on a Russian layout
  // would produce a confident decode for characters the reader never typed —
  // worse than any error message. Hangul and kana survive the fold as Hangul
  // and kana, get stripped by stripPattern, and are reported as unrecognised.
  // ---------------------------------------------------------------------------
  var LOOKALIKES = [
    // Hyphen-ish. Pasted codes carry these from PDFs, Word and listing pages.
    // NFKC leaves them alone, so they need naming.
    [/[‐‑‒–—―−]/g, "-"],
    [/[‘’‚‛ʹʼ′]/g, "'"],
    [/[“”„‟″]/g, "\""],
    // Every non-ASCII space, including the NBSP that a copy-paste out of a
    // rendered table leaves behind.
    [/[  ᠎ -   　]/g, " "],
    // Zero-width joiners and the BOM: invisible, and fatal to a length check.
    [/[​‌‍⁠﻿]/g, ""]
  ];

  /** Fold compatibility/width forms and the punctuation look-alikes to ASCII. */
  function foldInput(s) {
    if (typeof s.normalize === "function") {
      // Some engines throw on an unknown form rather than ignoring it.
      try { s = s.normalize("NFKC"); } catch (e) { /* fall through to the manual fold */ }
    }
    if (typeof s.normalize !== "function" || /[！-～]/.test(s)) {
      // No `normalize` (pre-ES6 engine), or it did not fold — do the one case
      // that actually matters by hand: the full-width ASCII block.
      s = s.replace(/[！-～]/g, function (ch) {
        return String.fromCharCode(ch.charCodeAt(0) - 0xFEE0);
      });
    }
    for (var i = 0; i < LOOKALIKES.length; i++) s = s.replace(LOOKALIKES[i][0], LOOKALIKES[i][1]);
    return s;
  }

  // Letters that are not Latin, so the input cannot be a code as printed on any
  // item we cover. Explicit ranges rather than \p{Script=} because this file is
  // ES2017 and ships unbuilt. Cyrillic, Greek, Hebrew, Arabic, Thai, Hangul
  // (jamo + syllables + halfwidth), kana, CJK ideographs, CJK compatibility.
  var NON_LATIN_LETTER =
    /[Ͱ-ϿЀ-ԯ֐-׿؀-ۿ฀-๿ᄀ-ᇿ぀-ヺー-ヿ㄰-㆏ㇰ-ㇿ㐀-䶿一-鿿ꥠ-꥿가-퟿豈-﫿ｦ-ￜ]/;
  // U+30FB "・" (katakana middle dot) is left OUT of the kana range above: it is
  // punctuation Japanese dealers print between style-code groups ("001・3306"),
  // not a letter, and a dotted Gucci code typed that way must keep decoding.

  /**
   * True when the RAW input carries a letter from a non-Latin script.
   *
   * Called on the raw string, before any folding, and used only to choose a
   * more useful message than "not recognised" — never to decode. Full-width
   * Latin (U+FF21–FF3A) is deliberately NOT included: that is Latin, it folds
   * cleanly, and it must decode.
   */
  function hasNonLatinLetters(raw) {
    return NON_LATIN_LETTER.test(String(raw == null ? "" : raw));
  }

  // Label words a reader types along with the code because they are printed
  // next to it or were asked for: "Serial No. 800000", "Ref. 116610", "N° 12".
  // Applied for EVERY brand, before the ruleset's own `stripWords`, because the
  // stress suite found that only one ruleset in twenty had declared any — and
  // the other nineteen told a reader their real code was invalid the moment
  // they typed its label. Multi-word entries are matched as phrases, so list a
  // phrase before its own words ("SERIAL NUMBER" before "NUMBER").
  // ⚠️ No bare two-letter word may go here: "NO" and "SN" are Louis Vuitton
  // factory codes, and "SN 2034" is how listings write the date code. A brand
  // whose label really is a two-letter word declares it in its own
  // `normalize.stripWords` (Coach's creed prints "No" before the number).
  var DEFAULT_STRIP_WORDS = [
    "MADE IN ITALY", "MADE IN FRANCE", "MADE IN SWITZERLAND", "MADE IN SPAIN",
    "SERIAL NUMBER", "SERIAL NO", "DATE CODE", "MODEL NUMBER", "STYLE NUMBER",
    "REFERENCE NUMBER", "REF NO", "CASE NUMBER", "CASE NO", "MOVEMENT NUMBER",
    "SERIAL", "NUMBER", "REFERENCE", "REF", "MODEL", "STYLE", "CODE", "ITEM",
    "CASE", "MOVEMENT", "CALIBER", "CALIBRE", "N°", "NUM"
  ];

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  /** "Vacheron Constantin" → ["VACHERON", "CONSTANTIN"]; accents dropped so a
   *  reader who types "Hermes" for "Hermès" is served the same. */
  function brandWords(rules) {
    var name = String((rules && rules.displayName) || "");
    if (typeof name.normalize === "function") {
      try { name = name.normalize("NFD").replace(/[̀-ͯ]/g, ""); } catch (e) { /* keep as is */ }
    }
    var out = [];
    var parts = name.toUpperCase().split(/[^A-Z0-9]+/);
    for (var i = 0; i < parts.length; i++) if (parts[i].length >= 3) out.push(parts[i]);
    return out;
  }

  function stripWordList(rules) {
    var n = (rules && rules.normalize) || {};
    var out = [];
    var seen = {};
    var add = function (w) { if (w && !seen[w]) { seen[w] = true; out.push(w); } };
    // Ruleset-declared first: a brand may know a phrase the defaults do not.
    if (Array.isArray(n.stripWords)) for (var a = 0; a < n.stripWords.length; a++) add(String(n.stripWords[a]).toUpperCase());
    for (var b = 0; b < DEFAULT_STRIP_WORDS.length; b++) add(DEFAULT_STRIP_WORDS[b]);
    var bw = brandWords(rules);
    for (var c = 0; c < bw.length; c++) add(bw[c]);
    return out;
  }

  /** Remove every label word/phrase from `s`; matching runs on an upper-cased
   *  twin so a ruleset that does not upper-case keeps its case. */
  function stripLabelWords(s, rules, alreadyUpper) {
    var words = stripWordList(rules);
    var up = alreadyUpper ? s : s.toUpperCase();
    for (var w = 0; w < words.length; w++) {
      var re = new RegExp("(^|[^A-Z0-9])" + escapeRegExp(words[w]) + "(?![A-Z0-9])", "g");
      var m;
      while ((m = re.exec(up)) !== null) {
        var start = m.index + m[1].length;
        var end = start + words[w].length;
        s = s.slice(0, start) + s.slice(end);
        up = up.slice(0, start) + up.slice(end);
        re.lastIndex = start;
      }
    }
    return s;
  }

  /**
   * Split a folded string into the part that can be a code and a verdict on
   * the letters that cannot.
   *
   * A whitespace-separated token made ONLY of non-Latin letters is a label the
   * reader typed beside the code ("Серия 1234567", "型番 2400000") and is
   * dropped, exactly as "Serial No." is. A token that MIXES a non-Latin letter
   * with Latin letters or digits ("АВ1234" on a Russian layout) is a code the
   * reader did not type: decoding what survives would answer a different code
   * with full confidence, so the whole input is refused instead. The UI already
   * shows the non-Latin card on INVALID_FORMAT when hasNonLatinLetters(raw).
   */
  function dropForeignLabels(s) {
    if (!NON_LATIN_LETTER.test(s)) return { text: s, contaminated: false };
    var tokens = s.split(/\s+/);
    var keep = [];
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (!t) continue;
      if (!NON_LATIN_LETTER.test(t)) { keep.push(t); continue; }
      if (/[A-Za-z0-9]/.test(t)) return { text: "", contaminated: true };
      // pure label token: dropped
    }
    return { text: keep.join(" "), contaminated: false };
  }

  function normalize(raw, rules) {
    var s = String(raw == null ? "" : raw);
    // FOLD FIRST, unconditionally and for every brand. This is not a ruleset
    // choice: no ruleset wants full-width digits thrown away.
    s = foldInput(s);
    s = dropForeignLabels(s).text;
    var n = (rules && rules.normalize) || {};
    if (n.trim !== false) s = s.trim();
    if (n.uppercase) s = s.toUpperCase();
    // ⚠️ Strip NOISE WORDS before punctuation, not after. People type what is on
    // the label and what they were asked for: "Style 493075", "gucci 493075",
    // "Serial No. 493075". Once stripPattern removes the space, "GUCCI493075" can
    // never match any pattern, and the reader is told their real code is invalid.
    // Order matters: after stripPattern there are no word boundaries left to find.
    // Matching is case-sensitive on an upper-cased string, so a ruleset that
    // does not upper-case keeps its words exactly as typed.
    s = stripLabelWords(s, rules, !!n.uppercase).trim();
    if (n.stripPattern) {
      // Ruleset-authored, validated at test time to compile and be bounded.
      s = s.replace(new RegExp(n.stripPattern, "g"), "");
    }
    return s;
  }

  /** Pull the ordered capture groups out of a match, ignoring the full match. */
  function groupsOf(m, names) {
    var out = {};
    var caps = Array.prototype.slice.call(m, 1);
    for (var i = 0; i < caps.length; i++) {
      var key = (names && names[i]) || ("g" + (i + 1));
      out[key] = caps[i];
    }
    return out;
  }

  function twoDigitYear(yy, pivot) {
    // pivot = the highest year the 1900s window may reach. Ruleset-supplied,
    // never inferred from the current date: decode must be deterministic, or
    // the golden-snapshot test becomes time-dependent and starts flapping.
    var n = parseInt(yy, 10);
    if (isNaN(n)) return null;
    var y1900 = 1900 + n, y2000 = 2000 + n;
    if (typeof pivot !== "number") return y2000;
    return y2000 <= pivot ? y2000 : y1900;
  }

  /**
   * WHY an op came back empty. An empty array used to be the whole answer, and
   * decode() could only say "matched the format but no documented value" —
   * true, and useless to a reader holding the item. An op now leaves a note on
   * the optional third argument saying which value it could not place:
   *
   *   { kind: "unlisted",   field, value, reason: "not-in-table" | "outside-era" }
   *   { kind: "impossible", field, value, reason: "invalid-month" | "invalid-week" | "future" }
   *
   * The note is a side channel, never a candidate: the return value keeps its
   * meaning, and an op called with two arguments (the tests do) behaves as it
   * always did. decode() reads the notes only when NOTHING decoded, so a miss in
   * one era never colours an answer another era found.
   */
  function noteMiss(ctx, rec) {
    if (ctx && ctx.misses) ctx.misses.push(rec);
  }

  /** The year after which a reading is "in the future". Absent ctx = no cap. */
  function nowYear(ctx) {
    return ctx && ctx.now ? ctx.now.year : Infinity;
  }

  // ---------------------------------------------------------------------------
  // The closed op set. Each returns an ARRAY of candidates (0..n), because
  // ambiguity is normal — a single-candidate return would force every op to
  // pretend it is certain.
  //
  // Candidate: { year?, yearFrom?, yearTo?, month?, week?, factory?, label?,
  //              confidence?, disambiguator? }
  // ---------------------------------------------------------------------------
  var OPS = {

    /** Nothing is encoded. Used by FORMAT_VALIDATE_ONLY brands (e.g. Gucci). */
    "none": function () { return []; },

    /**
     * Digit positions carry month/week and year, LV/Dior style.
     * Dior `09-MA-0161` → group "0161": positions 1+3 = month, 2+4 = year → 06/2011.
     */
    "positional-digits": function (p, g, ctx) {
      var src = String(g[p.source] || "");
      function pick(idxs) {
        if (!idxs) return null;
        var out = "";
        for (var i = 0; i < idxs.length; i++) {
          var c = src.charAt(idxs[i]);
          if (c === "") return null;
          out += c;
        }
        return out;
      }
      var yy = pick(p.yearIndexes);
      if (yy === null) return [];
      // `fullYear` for a code that spells the year out in four digits rather
      // than two. Goyard does: SUT 120162 is December 2016, where the outer two
      // digits are the month and the middle FOUR are the year, read in order.
      // Running that through the two-digit window would read "2016" as a
      // two-digit number and land in the year 4016.
      var year = p.fullYear ? parseInt(yy, 10) : twoDigitYear(yy, p.yearPivot);
      if (year === null || isNaN(year)) return [];

      var cand = { year: year };
      var mm = pick(p.monthIndexes);
      if (mm !== null) {
        var m = parseInt(mm, 10);
        if (m >= 1 && m <= 12) cand.month = m;
        else if (p.requireValidMonth) {
          // The month is part of what makes the code well-formed. If it cannot
          // exist, the code is not a real one, and returning the year anyway
          // would hand the user a confident-looking answer from bad input.
          noteMiss(ctx, { kind: "impossible", field: "month", value: mm, reason: "invalid-month", year: year });
          return [];
        } else {
          cand.monthInvalid = mm; // surfaced, not silently dropped
        }
      }
      var ww = pick(p.weekIndexes);
      if (ww !== null) {
        var w = parseInt(ww, 10);
        if (w >= 1 && w <= 53) cand.week = w;
        else {
          // Until round 3 an impossible week was DROPPED and the year returned
          // anyway, so an LV "SF5149" (week 54 of 2019) decoded as a confident
          // 2019. There is no week 54 and no week 00; a code that says so was
          // misread or invented, exactly as a month 13 is.
          noteMiss(ctx, { kind: "impossible", field: "week", value: ww, reason: "invalid-week", year: year });
          return [];
        }
      }
      // The year window is checked AFTER month and week, on purpose: a month
      // 15 cannot exist in any year, while a year outside the window is only
      // outside OUR tables. Checked the other way round, LV's SF5149 read
      // "1919, outside the month-code era" instead of "week 54", and the
      // reader was told the code might be unlisted when it cannot be real.
      //
      // Outside the era's window is "unlisted", not "impossible": SF1054 read
      // as a week code is 2004, a perfectly real year, just not one in which
      // LV printed week codes. Past the window AND past today is different —
      // that reading names a year that has not happened.
      if (typeof p.minYear === "number" && year < p.minYear) {
        noteMiss(ctx, { kind: "unlisted", field: "year", value: year, reason: "outside-era",
                        eraFrom: p.minYear, eraTo: p.maxYear });
        return [];
      }
      if (typeof p.maxYear === "number" && year > p.maxYear) {
        if (year > nowYear(ctx)) noteMiss(ctx, { kind: "impossible", field: "year", value: year, reason: "future", year: year });
        else noteMiss(ctx, { kind: "unlisted", field: "year", value: year, reason: "outside-era",
                             eraFrom: p.minYear, eraTo: p.maxYear });
        return [];
      }
      return [cand];
    },

    /** Numeric serial falls in [min,max] → an era label. */
    "range-lookup": function (p, g, ctx) {
      var n = parseInt(String(g[p.source] || "").replace(/\D/g, ""), 10);
      if (isNaN(n)) return [];
      var out = [];
      for (var i = 0; i < p.ranges.length; i++) {
        var r = p.ranges[i];
        if (n >= r.min && n <= r.max) {
          out.push({
            year: r.year, yearFrom: r.yearFrom, yearTo: r.yearTo,
            label: r.label, confidence: r.confidence
          });
        }
      }
      if (!out.length) noteMiss(ctx, { kind: "unlisted", field: p.source, value: String(n), reason: "not-in-table" });
      return out;
    },

    /**
     * A letter maps to a year, or to a YEAR WINDOW.
     *
     * A table value may be a number (one year) or a two-element array
     * [from, to]. Rolex needs the second form: its letter prefixes each cover
     * about two years and the windows overlap, because the letters were never
     * issued in a clean annual sequence. Returning the first year of the window
     * as if it were the answer would be a confident wrong date on half the
     * watches that use it.
     */
    "char-map": function (p, g, ctx) {
      var c = String(g[p.source] || "").charAt(0);
      var y = p.table[c];
      if (y == null) {
        if (c) noteMiss(ctx, { kind: "unlisted", field: p.source, value: c, reason: "not-in-table" });
        return [];
      }
      if (Array.isArray(y)) {
        if (y[0] === y[1]) return [{ year: y[0] }];
        return [{ yearFrom: y[0], yearTo: y[1] }];
      }
      return [{ year: y }];
    },

    /**
     * Letter + enclosing SHAPE maps to a year — the Hermès blind stamp.
     *
     * This op is why this project exists. Hermès reused the alphabet across
     * three cycles and disambiguated them with the frame drawn around the
     * letter (none / circle / square). No public tool on the internet takes
     * the shape as an input, so every one of them is guessing between years
     * that are decades apart.
     *
     * When the shape is unknown we return EVERY candidate across all shapes
     * rather than guessing, which is what produces the AMBIGUOUS state.
     */
    "char-shape-map": function (p, g, ctx) {
      var letter = String(g[p.letterSource] || "").charAt(0);
      var shape = String(g[p.shapeSource] || "").toLowerCase();
      if (!letter) return [];
      var out = [];
      var shapes = (shape && shape !== "unknown") ? [shape] : Object.keys(p.shapes);
      for (var i = 0; i < shapes.length; i++) {
        var s = shapes[i];
        var tbl = p.shapes[s];
        if (!tbl) continue;
        var hit = tbl[letter];
        if (hit == null) continue;
        // A shape/letter pair can itself be ambiguous (Hermès "none" spans
        // both the pre-1971 run and the post-2015 randomized run), so a table
        // entry is allowed to be an array of years.
        var years = Array.isArray(hit) ? hit : [hit];
        for (var j = 0; j < years.length; j++) {
          out.push({ year: years[j], shape: s, label: p.shapeLabels && p.shapeLabels[s] });
        }
      }
      // The Hermès "S in a square": the square cycle ran A to R, so the pair
      // is not in the table. The shape travels with the note because the
      // letter alone is not the unlisted value — S is fine in a circle.
      if (!out.length) {
        noteMiss(ctx, { kind: "unlisted", field: p.letterSource, value: letter, reason: "not-in-table",
                        shape: (shape && shape !== "unknown") ? shape : null });
      }
      return out;
    },

    /** Trailing MMYY. */
    "suffix-mmyy": function (p, g, ctx) {
      var s = String(g[p.source] || "");
      var mmyy = s.slice(-4);
      if (!/^\d{4}$/.test(mmyy)) return [];
      var m = parseInt(mmyy.slice(0, 2), 10);
      var year = twoDigitYear(mmyy.slice(2), p.yearPivot);
      if (year === null) return [];
      if (m < 1 || m > 12) {
        noteMiss(ctx, { kind: "impossible", field: "month", value: mmyy.slice(0, 2), reason: "invalid-month", year: year });
        return [];
      }
      return [{ year: year, month: m }];
    },

    /** Prefix → factory / country. Carries no date on its own. */
    "table-lookup": function (p, g, ctx) {
      var key = String(g[p.source] || "");
      var v = p.table[key];
      // An absent optional group (LV's 1980s codes may omit the factory) is not
      // an unlisted value — there is simply nothing to look up.
      if (v == null && key) noteMiss(ctx, { kind: "unlisted", field: p.source, value: key, reason: "not-in-table" });
      return v == null ? [] : [{ factory: v }];
    },

    /**
     * WHOLE-STRING table lookup into a NAMED field.
     *
     * `char-map` reads only the first character and always emits a year, which
     * covers Rolex and Hermès but not the very common shape where one capture
     * group is a month letter and another is a year of one OR two digits:
     *
     *   Coach   K8P-9870   K → October, 8 → 1998, P → plant
     *   Cartier AB94xxxx   94 → 1994
     *
     * The key is the ENTIRE captured group, so "4" and "04" are different keys
     * and mean different decades — which is exactly the distinction a Coach
     * creed turns on. `emit` names the field, so one op serves month, year and
     * anything later added, and the table stays an explicit, auditable list
     * rather than arithmetic with a pivot year. A value may be [from, to] to
     * declare a span the source itself does not resolve.
     *
     * Pair with `era.combine` (below) to fold the parts into one candidate.
     */
    "string-map": function (p, g, ctx) {
      var key = String(g[p.source] == null ? "" : g[p.source]);
      if (!key) return [];
      var v = p.table[key];
      if (v == null) {
        noteMiss(ctx, { kind: "unlisted", field: p.source, value: key, reason: "not-in-table" });
        return [];
      }
      var field = p.emit || "year";
      var out = {};
      if (Array.isArray(v)) {
        if (field !== "year") return [];
        if (v[0] === v[1]) out.year = v[0];
        else { out.yearFrom = v[0]; out.yearTo = v[1]; }
      } else {
        out[field] = v;
      }
      return [out];
    }
  };

  /**
   * Fold an era's per-part candidates into ONE.
   *
   * Ops return arrays because ambiguity is normal, and the default behaviour is
   * to CONCATENATE them — right when each op is an independent reading of the
   * same code. It is wrong when the ops are complementary parts of a single
   * reading: a month op and a year op would otherwise surface as two candidates,
   * "October" and "1998", instead of one October 1998.
   *
   * `"combine": true` on the era says the ops are parts, not alternatives.
   *
   * Guarded deliberately: if ANY part returned more than one candidate the parts
   * are genuinely ambiguous and there is no single correct way to pair them up,
   * so we leave them alone rather than inventing a combination the sources do
   * not support. A part that returned nothing is simply absent from the result,
   * which is how an unknown factory letter yields a date with no factory rather
   * than no answer at all.
   */
  function combineParts(cands) {
    if (cands.length < 2) return cands;
    var merged = {};
    for (var i = 0; i < cands.length; i++) {
      for (var k in cands[i]) {
        if (!Object.prototype.hasOwnProperty.call(cands[i], k)) continue;
        if (merged[k] == null) merged[k] = cands[i][k];
      }
    }
    return [merged];
  }

  // ---------------------------------------------------------------------------
  // FORMAT-CHECK: read a code SEGMENT BY SEGMENT. (2026-10-20.)
  //
  // Some brands print a style or article code that encodes no date at all but
  // is BUILT from named parts: Golden Goose's GMF00102.F000317.10220 is a brand
  // letter, a gender, a category, a model number, a material and a colour. The
  // honest tool for that is not a decoder. It checks the code is built the way
  // the brand builds it, names each part, and says plainly that a well-formed
  // code proves nothing about the item.
  //
  // None of the eight ops can say that. Each returns CANDIDATES, and a
  // candidate without a year is either a factory (siphoned off) or a DECODED
  // answer with a hole where the year belongs. So the segment table is
  // declarative ERA DATA, read here beside `requires` and `preempts`, and it
  // never changes the state on its own: an era with segments is still a
  // `noDate` era, so a match is FORMAT_OK_NO_DATE exactly as for Prada, plus a
  // `segments` list the result card can print.
  //
  //   "segments": [
  //     { "group": "gender", "label": "gender letter",
  //       "values": { "M": "men's", "W": "women's" }, "strict": true },
  //     { "group": "model", "label": "model number",
  //       "values": { "00102": "Super-Star" }, "unknown": "..." },
  //     { "group": "colour", "label": "colour code", "meaning": "..." }
  //   ]
  //
  // `values` is an explicit table, looked up by the WHOLE captured group and by
  // own property only, so "CONSTRUCTOR" can never hit Object.prototype.
  // `strict: true` makes the table part of the format: a value outside it means
  // the code is not one this brand prints, and the era does not match. Without
  // `strict` an unlisted value is reported as not in our table, which is the
  // honest answer for an open list such as model numbers, and is never called
  // a fault.
  // ---------------------------------------------------------------------------

  function readSegments(specs, g) {
    var out = [];
    for (var i = 0; i < specs.length; i++) {
      var s = specs[i];
      if (!isObj(s) || !s.group) continue;
      var raw = g[s.group];
      // An optional group that did not participate: nothing to report.
      if (raw == null || raw === "") continue;
      var value = String(raw);
      // `ignore` (opt-in, hallmark reader 2026-10-20): values that mean "the
      // reader gave no answer", such as a picker's "none" option. A segment can
      // read a non-code input (the group bag holds every field), and a picker
      // always sends something, so without this the card would print a line
      // for the option that says there is nothing to read.
      if (Array.isArray(s.ignore) && s.ignore.indexOf(value) !== -1) continue;
      // `compact: true` (opt-in, Hugo Boss article number): a rawPattern era
      // captures the separators the reader typed ("5051 9707"), so echo the
      // group without them. Segments without the flag are untouched.
      if (s.compact) value = value.replace(/[^A-Za-z0-9]/g, "");
      var seg = { group: s.group, label: s.label || s.group, value: value, meaning: s.meaning || null };
      // `link` (opt-in, G-Shock module number, 2026-10-21): the brand's own
      // page for THIS value, e.g. Casio's manual for module 3229. The template
      // comes from the ruleset; only the typed value is filled in, and it is
      // URI-encoded so a part can never break out of its query parameter.
      // Segments without the key are untouched, so every other result keeps
      // exactly its shape.
      if (isObj(s.link) && typeof s.link.href === "string" && /^https:\/\//.test(s.link.href)) {
        seg.link = {
          href: s.link.href.split("{value}").join(encodeURIComponent(value)),
          text: String(s.link.text || "").split("{value}").join(value)
        };
      }
      if (isObj(s.values)) {
        var hit = Object.prototype.hasOwnProperty.call(s.values, value) ? s.values[value] : null;
        seg.known = hit != null;
        if (hit != null) {
          seg.reading = hit;
        } else if (s.strict) {
          return { rejected: { group: s.group, label: seg.label, value: value, allowed: Object.keys(s.values) } };
        } else if (s.unknown) {
          seg.note = s.unknown;
        }
      }
      out.push(seg);
    }
    return { segments: out };
  }

  // ---------------------------------------------------------------------------
  // Dedupe + resolve
  // ---------------------------------------------------------------------------

  function candKey(c) {
    return [c.year, c.yearFrom, c.yearTo, c.month, c.week, c.factory, c.shape, c.label].join("|");
  }

  function dedupe(cands) {
    var seen = {}, out = [];
    for (var i = 0; i < cands.length; i++) {
      var k = candKey(cands[i]);
      if (seen[k]) continue;
      seen[k] = 1;
      out.push(cands[i]);
    }
    return out;
  }

  function mergeFactory(cands, factories) {
    if (!factories.length) return cands;
    var f = factories[0].factory;
    for (var i = 0; i < cands.length; i++) {
      if (cands[i].factory == null) cands[i].factory = f;
    }
    return cands;
  }

  // ---------------------------------------------------------------------------
  // decode()
  // ---------------------------------------------------------------------------

  /**
   * @param {string|object} input  raw code, or {code, shape, ...} for multi-input brands
   * @param {object} rules         a ruleset from rules/<brand>.json
   * @returns {object} { state, brand, codeNoun, input, candidates, eras,
   *                     disambiguator, guidance, disclaimer, notes }
   */
  /**
   * True when every field named in `requires` either is unset / "unknown" (the
   * user has not told us) or holds one of the listed values.
   */
  function eraAllowed(requires, fields, answered) {
    for (var key in requires) {
      if (!Object.prototype.hasOwnProperty.call(requires, key)) continue;
      var have = fields[key];
      // `answered` (opt-in `era.requiresAnswer`, hallmark reader 2026-10-20):
      // an era that reads NOTHING but the field (a picture mark with no typed
      // code) has nothing to stand on when the field is unset, so there an
      // unset field rules the era out instead of letting it through.
      if (have == null || have === "" || have === "unknown") { if (answered) return false; continue; }
      var want = requires[key];
      if (!Array.isArray(want)) want = [want];
      var hit = false;
      for (var i = 0; i < want.length; i++) if (want[i] === have) { hit = true; break; }
      if (!hit) return false;
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // The clock. INJECTED, never read inside an op.
  //
  // twoDigitYear() is deliberately clock-free so the golden snapshots never
  // flap, and that stays true: the clock is used for exactly one question —
  // "has this date happened yet?" — and a test passes `opts.now` to pin it.
  // UTC so a reader in Tokyo and one in Los Angeles get the same answer on the
  // same day.
  // ---------------------------------------------------------------------------
  function clockOf(d) {
    if (!(d instanceof Date) || isNaN(d.getTime())) d = new Date();
    var y = d.getUTCFullYear();
    var doy = Math.floor((Date.UTC(y, d.getUTCMonth(), d.getUTCDate()) - Date.UTC(y, 0, 1)) / 86400000) + 1;
    // Week of the year, PLUS ONE. No source says whose week numbering the
    // brands use (ISO or calendar), and the two differ by one at most, so the
    // slack is spent on never calling this week's genuine code "future".
    return { year: y, month: d.getUTCMonth() + 1, week: Math.ceil(doy / 7) + 1 };
  }

  /** The first year a candidate can mean: `year`, or the start of its window. */
  function spanStart(c) {
    return typeof c.year === "number" ? c.year : c.yearFrom;
  }

  function isFuture(c, now) {
    var y = spanStart(c);
    if (typeof y !== "number") return false;
    if (y > now.year) return true;
    // A window that starts this year is not in the future; only an exact
    // year can be future by its month or week.
    if (y < now.year || typeof c.year !== "number") return false;
    if (typeof c.month === "number" && c.month > now.month) return true;
    if (typeof c.week === "number" && c.week > now.week) return true;
    return false;
  }

  function endedYear(rules) {
    var e = rules && rules.codeEndedAt;
    var y = e ? parseInt(String(e.date), 10) : NaN;
    return isNaN(y) ? null : y;
  }

  /**
   * Turn the ops' notes into WHICH miss this is. Called only when the layout
   * matched and nothing decoded.
   *
   * IMPOSSIBLE_DATE needs EVERY failing era to have failed impossibly. LV's
   * SF1054 is month 15 read as a month code but 2004 read as a week code, and
   * 2004 is a real year: calling that code "impossible" would be stronger than
   * the evidence, so it is UNLISTED. ZZ9999 is month 99 one way and 2099 the
   * other, and neither can exist, so it is IMPOSSIBLE.
   *
   * The unlisted value shown is the first value that is NOT IN A TABLE (an LV
   * factory "ZZ", a Hermès letter) before a year outside an era, because the
   * table miss is the one a reader can check on the item.
   */
  function classifyMiss(result, eraMisses, rules) {
    if (!eraMisses.length) return;
    var allImpossible = true, firstImp = null, firstTable = null, firstEra = null;
    for (var i = 0; i < eraMisses.length; i++) {
      var hasImp = false;
      for (var j = 0; j < eraMisses[i].misses.length; j++) {
        var m = eraMisses[i].misses[j];
        if (m.kind === "impossible") { hasImp = true; if (!firstImp) firstImp = { m: m, era: eraMisses[i].era }; }
        else if (m.reason === "not-in-table") { if (!firstTable) firstTable = { m: m, era: eraMisses[i].era }; }
        else if (!firstEra) firstEra = { m: m, era: eraMisses[i].era };
      }
      if (!hasImp) allImpossible = false;
    }
    if (allImpossible && firstImp) {
      var im = firstImp.m;
      result.state = STATE.IMPOSSIBLE_DATE;
      result.impossible = { reason: im.reason, field: im.field, value: im.value, era: firstImp.era, year: im.year };
      if (im.reason === "invalid-month") result.impossible.month = parseInt(im.value, 10);
      if (im.reason === "invalid-week") result.impossible.week = parseInt(im.value, 10);
      result.notes.push("decodes-to-impossible-date");
      return;
    }
    var u = firstTable || firstEra;
    if (!u) return;
    result.state = STATE.UNLISTED_VALUE;
    result.unlisted = { field: u.m.field, value: u.m.value, reason: u.m.reason, era: u.era };
    if (u.m.reason === "outside-era") {
      if (typeof u.m.eraFrom === "number") result.unlisted.eraFrom = u.m.eraFrom;
      if (typeof u.m.eraTo === "number") result.unlisted.eraTo = u.m.eraTo;
      // A reading past the year the brand stopped printing codes is the LV
      // case the guidance already explains (small unlined pieces kept a
      // printed code after March 2021), so the UI can say so instead of a
      // bare "not in our tables".
      var ey = endedYear(rules);
      if (ey !== null && typeof u.m.value === "number" && u.m.value > ey) {
        result.unlisted.afterCodeEnded = true;
        result.notes.push("unlisted-after-code-ended");
      }
    }
    if (u.m.shape) result.unlisted.shape = u.m.shape;
    result.notes.push("layout-matched-value-unlisted");
  }

  // ---------------------------------------------------------------------------
  // CONFUSABLE SUGGESTIONS — "Did you mean SF2134?"
  //
  // The classic misreads of a worn stamp or a phone photo. ONE swap at a time,
  // because two simultaneous guesses are no longer a misread, they are a new
  // code; and a suggestion counts only if it decodes to a non-miss state, so
  // the chip never offers a code the tool would then refuse. Never applied
  // automatically: the reader taps it or does not.
  //
  // Bounded on purpose: at most two swaps per character on an input of at
  // most 24 characters, each a plain decode() with suggestions switched off,
  // so a suggestion can never recurse.
  // ---------------------------------------------------------------------------
  var CONFUSABLE = {
    "O": ["0"], "0": ["O"], "I": ["1"], "1": ["I", "L"], "L": ["1"],
    "S": ["5"], "5": ["S"], "B": ["8"], "8": ["B"], "Z": ["2"], "2": ["Z"],
    "G": ["6"], "6": ["G"]
  };
  // A dated answer outranks a format-only one: it is the stronger evidence
  // that the swap is what the item actually says.
  var CLEAN_RANK = { DECODED: 2, AMBIGUOUS: 2, FORMAT_OK_NO_DATE: 1 };
  var MAX_SUGGESTIONS = 2;

  /**
   * A misread puts a character of the WRONG CLASS among its neighbours: the
   * "I" in SF2I34 sits between digits, the "O" in 4930O5 likewise. Swapping a
   * letter inside a run of letters is not a misread, it is a different word —
   * and on a permissive pattern (Prada accepts most mixed strings) it turned
   * "HELLO" into a confident "Did you mean HE1LO?". So a digit may replace a
   * letter only beside a digit, and a letter a digit only beside a letter.
   * Separators are skipped; a one-character input has no neighbours to ask.
   */
  function fitsNeighbours(src, i, alt) {
    var wantDigit = /[0-9]/.test(alt);
    var seen = false;
    for (var dir = -1; dir <= 1; dir += 2) {
      for (var j = i + dir; j >= 0 && j < src.length; j += dir) {
        var ch = src.charAt(j);
        if (!/[A-Za-z0-9]/.test(ch)) continue;
        seen = true;
        if (/[0-9]/.test(ch) === wantDigit) return true;
        break;
      }
    }
    return !seen;
  }

  function suggest(fields, src, rules, opts) {
    src = String(src || "");
    if (!src || src.length > 24) return [];
    var seen = {};
    seen[normalize(src, rules)] = true;
    var found = [];
    for (var i = 0; i < src.length; i++) {
      var alts = CONFUSABLE[src.charAt(i).toUpperCase()];
      if (!alts) continue;
      for (var a = 0; a < alts.length; a++) {
        if (!fitsNeighbours(src, i, alts[a])) continue;
        var cand = src.slice(0, i) + alts[a] + src.slice(i + 1);
        var key = normalize(cand, rules);
        if (!key || seen[key]) continue;
        seen[key] = true;
        var f = {};
        for (var k in fields) if (Object.prototype.hasOwnProperty.call(fields, k)) f[k] = fields[k];
        f.code = cand;
        var r = decode(f, rules, { now: opts.now, noSuggest: true });
        if (CLEAN_RANK[r.state]) found.push({ code: cand, state: r.state, rank: CLEAN_RANK[r.state], pos: found.length });
      }
    }
    // `pos` breaks ties explicitly: Array.prototype.sort is only guaranteed
    // stable from ES2019, and this file is ES2017.
    found.sort(function (x, y) { return (y.rank - x.rank) || (x.pos - y.pos); });
    var out = [];
    for (var s = 0; s < found.length && out.length < MAX_SUGGESTIONS; s++) out.push({ code: found[s].code, state: found[s].state });
    return out;
  }

  function decode(input, rules, opts) {
    if (!isObj(rules)) throw new Error("decode(): a ruleset is required");
    opts = isObj(opts) ? opts : {};
    var now = clockOf(opts.now);

    var fields = isObj(input) ? input : { code: input };
    var code = normalize(fields.code, rules);

    // A SEPARATOR-PRESERVING form of the same input.
    //
    // normalize() strips punctuation, which is right for almost every brand —
    // but some markings encode their era IN the punctuation. Gucci's 1980s
    // Accessory Collection number "89.02.018" normalises to "8902018", seven
    // digits, which then matches the MODERN leather-tab serial rule and gets
    // reported as a modern bag. A confident wrong era is worse than a shrug and
    // is invisible to any "how many inputs do we recognise?" metric, because it
    // counts as a hit.
    //
    // An era may therefore carry `rawPattern`, matched against THIS string
    // instead of the normalised one. Same regex op, different target — no new
    // op, and nothing executable in JSON.
    // Folded the same way normalize() folds — a full-width or zero-width
    // character must not decide which era answers (stress suite, DEFECT-2).
    var looseSrc = dropForeignLabels(foldInput(String(fields.code == null ? "" : fields.code)));
    var loose = stripLabelWords(looseSrc.text.toUpperCase().replace(/\s+/g, " ").trim(), rules, true)
      .replace(/^[\s.:#\-]+|[\s.:#\-]+$/g, "");
    if (looseSrc.contaminated) code = "";

    var result = {
      state: STATE.INVALID_FORMAT,
      brand: rules.brand,
      displayName: rules.displayName,
      codeNoun: rules.codeNoun,
      tier: rules.tier,
      input: code,
      candidates: [],
      eras: [],
      notes: [],
      disclaimer: rules.disclaimer || null
    };

    // An empty code is normally nothing to work with. But a brand can be driven
    // by something other than a typed code: Fendi answers from which marking is
    // on the tag, and its serial is explicitly optional. So bail only when the
    // ruleset actually requires a code.
    var codeRequired = true;
    for (var q = 0; q < (rules.inputs || []).length; q++) {
      if (rules.inputs[q].name === "code") { codeRequired = rules.inputs[q].required !== false; break; }
    }
    if (!code && codeRequired) {
      result.state = STATE.INVALID_FORMAT;
      result.notes.push("empty-input");
      return result;
    }

    // A brand that stopped issuing codes gets a first-class answer, not a
    // shrug. This is the single biggest content gap across every competitor:
    // they all state the fact and then leave the user with nowhere to go.
    var ended = rules.codeEndedAt;

    var matchedEras = [], allCands = [], factories = [], eraMisses = [];
    // The first strict segment that turned a matching shape away. Reported
    // only if NOTHING else matched, so the card can say which part is wrong.
    var rejectedSegment = null;
    // Whether EVERY era that matched declares it carries no date. A brand can
    // be mixed: Rolex decodes 1926 to 2009 and randomised its serials in 2010,
    // so the tier alone cannot say whether a given input is dateable.
    var allMatchedAreNoDate = true;

    for (var i = 0; i < (rules.eras || []).length; i++) {
      var era = rules.eras[i];

      // `era.requires` — a declarative gate on a NON-code input.
      //
      // Some brands document two formats whose regexes genuinely overlap, so
      // no pattern can separate them. Gucci is the case: a two-row serial is
      // six digits over six, and a single-row wallet serial is 6 to 13 digits,
      // so every two-row code also matches the one-row pattern and the tool
      // reported both eras at once, unable to tell the user which half of its
      // own reference table applied to them.
      //
      // The fix is the one the live Fendi decoder uses for exactly this
      // problem: ask the user what they are looking at. This is the
      // declarative half of that — no new op, no executable logic in JSON,
      // just "this era only applies when field X is one of these values".
      //
      // Absent, empty or "unknown" NEVER rules an era out: not knowing is not
      // evidence, and the honest answer to "I'm not sure" is every era the
      // code could belong to.
      if (isObj(era.requires) && !eraAllowed(era.requires, fields, era.requiresAnswer === true)) continue;

      var re;
      var pat = era.rawPattern || era.pattern;
      try { re = new RegExp(pat); } catch (e) { continue; }
      var m = re.exec(era.rawPattern ? loose : code);
      if (!m) continue;

      var g = groupsOf(m, era.groupNames);
      // Extra (non-code) inputs such as Hermès stamp shape join the group bag
      // so ops address everything through one namespace.
      for (var k in fields) {
        if (Object.prototype.hasOwnProperty.call(fields, k) && k !== "code") g[k] = fields[k];
      }

      // FORMAT-CHECK segments. A strict segment outside its table means this
      // era does not match, exactly as if the regex had failed.
      var segs = null;
      if (Array.isArray(era.segments)) {
        var segRead = readSegments(era.segments, g);
        if (segRead.rejected) {
          if (!rejectedSegment) {
            rejectedSegment = segRead.rejected;
            rejectedSegment.era = era.id;
          }
          continue;
        }
        segs = segRead.segments;
      }

      var decodes = Array.isArray(era.decode) ? era.decode : [era.decode];
      var eraCands = [];
      var raw = [];
      var parts = [];
      var ctx = { now: now, misses: [] };
      var anyPartAmbiguous = false;
      var anyPartEmpty = false;
      for (var d = 0; d < decodes.length; d++) {
        var spec = decodes[d];
        if (!spec || !OPS[spec.op]) continue;
        var partCands = OPS[spec.op](spec.params || {}, g, ctx) || [];
        if (partCands.length > 1) anyPartAmbiguous = true;
        if (!partCands.length) anyPartEmpty = true;
        parts.push(partCands);
        raw = raw.concat(partCands);
      }
      // `"combine": "cross"` (opt-in, Seiko 2026-10-21): the parts are one
      // reading, but ONE part may honestly have several values. A Seiko serial
      // gives the month for certain and only the last digit of the year, so
      // the year part returns every year that fits and the month part one
      // month. Pairing each year with the month ("November 1999 or November
      // 2009") is the only combination there is, so it is not invented.
      // Allowed only when at most one part is ambiguous; two ambiguous parts
      // would need a pairing the sources do not give, so they stay unpaired.
      if (era.combine === "cross" && !anyPartEmpty) {
        var many = 0;
        for (var pa = 0; pa < parts.length; pa++) if (parts[pa].length > 1) many++;
        if (many <= 1) {
          var crossed = [{}];
          for (var pb = 0; pb < parts.length; pb++) {
            var next = [];
            for (var pc = 0; pc < crossed.length; pc++) {
              for (var pd2 = 0; pd2 < parts[pb].length; pd2++) {
                var mergedPart = {};
                var srcA = crossed[pc], srcB = parts[pb][pd2], key;
                for (key in srcA) if (Object.prototype.hasOwnProperty.call(srcA, key)) mergedPart[key] = srcA[key];
                for (key in srcB) if (Object.prototype.hasOwnProperty.call(srcB, key) && mergedPart[key] == null) mergedPart[key] = srcB[key];
                next.push(mergedPart);
              }
            }
            crossed = next;
          }
          raw = crossed;
          anyPartAmbiguous = false;
        }
      }
      // Complementary parts (month op + year op + factory op) fold into one
      // reading. Done BEFORE the factory siphon below, or Coach's plant letter
      // would be split off into `factories` and the date would lose it.
      //
      // A MISSING PART KILLS THE READING. The parts of a `combine` era are one
      // answer, not alternatives, so an incomplete set is not a partial answer
      // — it is no answer. Without this guard a Coach creed whose year digits
      // fall outside the table (L08K-4567, a 2008 bag) merged the month op's
      // output alone and returned DECODED with `{month: 11}` and NO YEAR: the
      // card said "November" and left a silent hole where the year belongs.
      // Returning nothing lets the code fall through to the honest path.
      if (era.combine && anyPartEmpty) raw = [];
      else if (era.combine && era.combine !== "cross" && !anyPartAmbiguous) raw = combineParts(raw);
      {
        var got = raw;
        for (var c = 0; c < got.length; c++) {
          if (got[c].factory != null && got[c].year == null) { factories.push(got[c]); continue; }
          got[c].era = era.id;
          got[c].eraLabel = era.label;
          if (got[c].confidence == null) got[c].confidence = era.confidence || "documented";
          eraCands.push(got[c]);
        }
      }

      var matched = { id: era.id, label: era.label, sourceIds: era.sourceIds || [],
                      preempts: era.preempts || null };
      // Only on eras that declare segments, so every other brand's result keeps
      // exactly the shape it had (the golden snapshots compare it whole).
      if (segs) matched.segments = segs;
      matchedEras.push(matched);
      // A no-date era has nothing to be unlisted: Gucci's style-code eras run
      // a string-map over an EMPTY table on purpose, and its "miss" is the
      // honest FORMAT_OK_NO_DATE answer, not an unlisted value.
      if (!era.noDate && ctx.misses.length) eraMisses.push({ era: era.id, misses: ctx.misses });
      if (!era.noDate) allMatchedAreNoDate = false;

      if (era.state === STATE.OUT_OF_SCOPE) {
        result.state = STATE.OUT_OF_SCOPE;
        result.eras = matchedEras;
        result.notes.push(era.note || "out-of-scope");
        return result;
      }
      allCands = allCands.concat(eraCands);
    }

    // A more specific marking SILENCES the generic ones it would be confused
    // with. Gucci's dotted 1980s Accessory Collection number also matches the
    // modern serial rule once its dots are gone; listing both would tell a user
    // their 1980s bag might be modern. Declarative, one direction, and a
    // preempting era must have matched on its own evidence first.
    var preempted = {};
    for (var pe = 0; pe < matchedEras.length; pe++) {
      var pl = matchedEras[pe].preempts;
      if (pl) for (var pj = 0; pj < pl.length; pj++) preempted[pl[pj]] = true;
    }
    if (Object.keys(preempted).length) {
      matchedEras = matchedEras.filter(function (e) { return !preempted[e.id]; });
      allCands = allCands.filter(function (c) { return !preempted[c.era]; });
      allMatchedAreNoDate = true;
      for (var pk = 0; pk < matchedEras.length; pk++) {
        for (var pn = 0; pn < (rules.eras || []).length; pn++) {
          if (rules.eras[pn].id === matchedEras[pk].id && !rules.eras[pn].noDate) {
            allMatchedAreNoDate = false;
          }
        }
      }
    }
    for (var pd = 0; pd < matchedEras.length; pd++) delete matchedEras[pd].preempts;

    result.eras = matchedEras;
    for (var sg = 0; sg < matchedEras.length; sg++) {
      if (matchedEras[sg].segments) { result.segments = matchedEras[sg].segments; break; }
    }

    if (!matchedEras.length) {
      result.state = STATE.INVALID_FORMAT;
      if (rejectedSegment) {
        result.rejectedSegment = rejectedSegment;
        result.notes.push("segment-not-allowed");
      }
      if (ended) {
        result.guidance = ended.guidance;
        result.replacedBy = ended.replacedBy;
        result.notes.push("check-code-ended");
      }
      if (!opts.noSuggest && !looseSrc.contaminated) result.suggestions = suggest(fields, loose || code, rules, opts);
      return result;
    }

    allCands = mergeFactory(dedupe(allCands), factories);
    allCands.sort(function (a, b) { return (a.year || a.yearFrom || 0) - (b.year || b.yearFrom || 0); });

    // A reading in the FUTURE is not a reading. Two-digit years need a pivot,
    // and Dior's and Saint Laurent's sit at 2029 so the tables stay valid for
    // a few years — which means a 0127 reads as January 2027 today. Drop such a
    // candidate; if it was the only one, the code names a date that has not
    // happened yet, and the reader is told exactly that.
    var future = [];
    allCands = allCands.filter(function (c) {
      if (isFuture(c, now)) { future.push(c); return false; }
      return true;
    });
    if (future.length && !allCands.length) {
      var f0 = future[0];
      result.state = STATE.IMPOSSIBLE_DATE;
      result.impossible = { reason: "future", field: "year", value: spanStart(f0),
                            year: spanStart(f0), month: f0.month, week: f0.week };
      if (f0.factory != null) result.factory = f0.factory;
      result.notes.push("decodes-to-future-date");
      return result;
    }
    if (future.length) result.notes.push("future-reading-dropped");
    result.candidates = allCands;

    if (!allCands.length) {
      // FORMAT_VALIDATE_ONLY is a whole-brand statement; `era.noDate` is the
      // per-era one, for brands that encoded a date for part of their history
      // and then stopped. Either is a legitimate "we recognise this and there
      // is nothing to read", as opposed to "we do not recognise this".
      if (rules.tier === "FORMAT_VALIDATE_ONLY" || rules.tier === "FORMAT_CHECK" ||
          (matchedEras.length && allMatchedAreNoDate)) {
        // Format recognised, and nothing dateable was ever encoded. This is the
        // honest, correct answer for brands whose serial is a style/lot
        // identifier (Gucci) — it is NOT an error, and presenting it as one
        // would teach users the wrong thing about their item.
        result.state = STATE.FORMAT_OK_NO_DATE;
        if (factories.length) result.factory = factories[0].factory;
        // Prefer the note belonging to the era that actually matched. The
        // brand-level noDateNote describes Gucci's LEATHER TAB serial, so
        // showing it for a black fabric tag or an Accessory Collection code
        // told the reader about the wrong marking entirely. An era only
        // overrides when it says something specific; otherwise fall back.
        var eraNote = null;
        for (var nz = 0; nz < matchedEras.length && !eraNote; nz++) {
          for (var nq = 0; nq < (rules.eras || []).length; nq++) {
            if (rules.eras[nq].id === matchedEras[nz].id && rules.eras[nq].note) {
              eraNote = rules.eras[nq].note;
              break;
            }
          }
        }
        result.notes.push(eraNote || rules.noDateNote || "format-valid-no-date-encoded");
        return result;
      }
      // A date-bearing brand whose pattern matched but whose value resolves to
      // nothing means the combination is not in the documented tables —
      // e.g. a Hermès "S" in a square, when the square cycle only ran A–R.
      // Saying "valid format" here would imply we recognised something we did
      // not, so this is an explicit miss. WHICH miss is decided by the notes
      // the ops left (classifyMiss); with no notes it stays INVALID_FORMAT and
      // reads exactly as it did before round 3.
      result.state = STATE.INVALID_FORMAT;
      if (factories.length) result.factory = factories[0].factory;
      result.notes.push("matched-format-but-no-documented-value");
      if (ended) {
        result.guidance = ended.guidance;
        result.replacedBy = ended.replacedBy;
      }
      classifyMiss(result, eraMisses, rules);
      if (!opts.noSuggest && result.state !== STATE.IMPOSSIBLE_DATE) {
        result.suggestions = suggest(fields, loose || code, rules, opts);
      }
      return result;
    }

    // ROUND 4 (2026-10-29). A reading that DECODES while its factory lookup
    // missed. LV "AM2017" reads as week 21 of 2007, but AM is in no factory
    // table, and until now the card said so nowhere: the visitor saw a normal
    // dated answer and could read that as "a real code". Searchers type exactly
    // this ("AM2017 authenticity"), so the result names the unlisted letters.
    // Only a `table-lookup` miss counts (the factory op), and only when the
    // winning reading from that era has no factory, so a code whose factory IS
    // listed keeps exactly the result shape it had.
    for (var um = 0; um < eraMisses.length && !result.unlistedPart; um++) {
      var umEra = null;
      for (var ue = 0; ue < (rules.eras || []).length; ue++) if (rules.eras[ue].id === eraMisses[um].era) umEra = rules.eras[ue];
      if (!umEra) continue;
      var umSpecs = Array.isArray(umEra.decode) ? umEra.decode : [umEra.decode];
      for (var un = 0; un < eraMisses[um].misses.length && !result.unlistedPart; un++) {
        var mk = eraMisses[um].misses[un];
        if (mk.reason !== "not-in-table") continue;
        var isLookup = false;
        for (var us = 0; us < umSpecs.length; us++) {
          if (umSpecs[us] && umSpecs[us].op === "table-lookup" && umSpecs[us].params && umSpecs[us].params.source === mk.field) isLookup = true;
        }
        if (!isLookup) continue;
        for (var uc = 0; uc < allCands.length; uc++) {
          if (allCands[uc].era === umEra.id && allCands[uc].factory == null) {
            result.unlistedPart = { field: mk.field, value: mk.value, era: umEra.id };
            result.notes.push("factory-not-in-table");
            break;
          }
        }
      }
    }

    // CRITIC PASS (2026-10-29). A reading that falls AFTER the month the brand
    // stopped printing codes. LV "AR2221" reads as week 22 of 2021, but the era
    // itself is "2007 to March 2021" and LV moved most items to a chip that
    // March, so the card must not present it as an ordinary date. The reading
    // stays (small unlined pieces kept a printed code, and resale sources say
    // some 2021 pieces still carry one), only a line is added. Opt-in per
    // ruleset (`codeEndedAt.flagLaterReadings`), because on the watch rulesets
    // `codeEndedAt` is where the published record ends, not the brand's numbering.
    var cea = rules.codeEndedAt;
    var ceaM = cea && cea.flagLaterReadings === true && /^(\d{4})-(\d{2})$/.exec(String(cea.date));
    if (ceaM) {
      var endY = parseInt(ceaM[1], 10), endMo = parseInt(ceaM[2], 10);
      for (var ac = 0; ac < allCands.length && !result.afterCodeEnded; ac++) {
        var cc = allCands[ac];
        if (typeof cc.year !== "number" || cc.yearFrom != null || cc.yearTo != null) continue;
        var after = false;
        if (cc.year > endY) after = true;
        else if (cc.year === endY) {
          // Earliest day the reading can mean, compared with the last day of
          // the ended month: week w starts no earlier than 1 Jan + 7(w-1) days.
          if (typeof cc.month === "number") after = cc.month > endMo;
          else if (typeof cc.week === "number") {
            var wStart = Date.UTC(endY, 0, 1) + (cc.week - 1) * 7 * 86400000;
            after = wStart > Date.UTC(endY, endMo, 0);
          }
        }
        if (after) {
          result.afterCodeEnded = { endedAt: cea.date, era: cc.era };
          result.notes.push("reads-after-code-ended");
        }
      }
    }

    if (allCands.length === 1) {
      result.state = STATE.DECODED;
      return result;
    }

    result.state = STATE.AMBIGUOUS;
    // The disambiguating question is the whole point: it turns "we don't know"
    // into "answer this and we will".
    result.disambiguator = rules.ambiguityNote || null;
    return result;
  }

  // ---------------------------------------------------------------------------
  // Era-table rows — the SAME ruleset that drives decode() renders the static
  // table, so the two can never disagree. The table is server-rendered in
  // Liquid for SEO (AI crawlers do not execute JavaScript); this function
  // exists so the browser and the build can share one definition.
  // ---------------------------------------------------------------------------
  function eraTable(rules) {
    return (rules.eras || []).map(function (e) {
      return {
        id: e.id,
        era: e.label,
        format: e.formatHuman || "",
        example: e.example || "",
        location: e.location || "",
        tells: e.tells || "",
        confidence: e.confidence || "documented",
        sourceIds: e.sourceIds || []
      };
    });
  }

  // ---------------------------------------------------------------------------
  // CROSS-CHECK — "Does the code fit the item?"
  //
  // The reader tells us what the listing says (model, country, year, the
  // number on the card, a chip) and we say whether the code agrees. Three
  // outcomes and never a fourth: `fits`, `conflict`, `unclear`. Nothing here
  // can say fake. A conflict is a reason for a closer look — codes get misread
  // and listings get the year wrong — and the UI prints that with every one.
  //
  // The same philosophy as the ops: a CLOSED set of five check types, each
  // driven by data in the ruleset's optional `crossCheck` block, and no brand
  // name anywhere in this code. A brand that needs a sixth type is a reason to
  // re-read the source, not to add a branch.
  //
  // What it does that no competitor does: it NARROWS. An AMBIGUOUS Hermès "Y"
  // is 1969 or 2020; tell us it is a Lindy (2007 onwards) and only 2020 is
  // consistent with both the code and the item. The survivors come back as
  // `narrowed`, and one survivor out of several is shown as THE reading.
  //
  // It runs only on a reading. A miss state has no candidate years to compare,
  // and a period check that passes an impossible date (a competitor's bug) is
  // worse than none, so every year-based check on a miss is `unclear`.
  // ---------------------------------------------------------------------------
  var CROSS_CHECK_TYPES = ["model", "madeIn", "claimedYear", "pairCode", "feature"];

  function xcConfig(rules) { return isObj(rules && rules.crossCheck) ? rules.crossCheck : {}; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function given(v) { return v != null && String(v).trim() !== ""; }
  function tolOf(v, d) { return typeof v === "number" && v >= 0 ? v : d; }

  /**
   * [from, to] of a candidate, or null when it carries no date. ⚠️ A candidate
   * may carry a WINDOW instead of a year (Rolex letters, Omega bands), so
   * reading `c.year` alone would treat half the watch readings as undated.
   */
  function spanOf(c, now) {
    if (typeof c.year === "number") return [c.year, c.year];
    var f = typeof c.yearFrom === "number" ? c.yearFrom : null;
    var t = typeof c.yearTo === "number" ? c.yearTo : null;
    if (f === null && t === null) return null;
    return [f === null ? -Infinity : f, t === null ? now.year : t];
  }

  function overlaps(a, b, tol) { return a[1] >= b[0] - tol && a[0] <= b[1] + tol; }

  /** Upper-case, accents off, punctuation to single spaces: "Lindy 26" ≡ "LINDY-26". */
  function normName(s) {
    var n = String(s == null ? "" : s);
    if (typeof n.normalize === "function") {
      try { n = n.normalize("NFD").replace(/[̀-ͯ]/g, ""); } catch (e) { /* keep as is */ }
    }
    return n.toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();
  }

  /** "France, Italy or Switzerland" → ["France", "Italy", "Switzerland"]. */
  function splitCountries(s) {
    var parts = String(s == null ? "" : s).split(/\s*,\s*|\s+or\s+|\s+and\s+/);
    var out = [];
    for (var i = 0; i < parts.length; i++) if (parts[i].trim()) out.push(parts[i].trim());
    return out;
  }

  function pushUnique(list, v) {
    for (var i = 0; i < list.length; i++) if (normName(list[i]) === normName(v)) return;
    list.push(v);
  }

  /**
   * Every country the ruleset can name, DERIVED from its factory tables so the
   * list can never disagree with what decode() reports. `brandCountries` is
   * only for a brand with no factory code at all; it adds, it never replaces.
   */
  function countriesOf(rules) {
    var out = [];
    var eras = arr(rules && rules.eras);
    for (var i = 0; i < eras.length; i++) {
      var specs = Array.isArray(eras[i].decode) ? eras[i].decode : [eras[i].decode];
      for (var j = 0; j < specs.length; j++) {
        if (!specs[j] || specs[j].op !== "table-lookup" || !specs[j].params) continue;
        var tbl = specs[j].params.table || {};
        for (var k in tbl) {
          if (!Object.prototype.hasOwnProperty.call(tbl, k)) continue;
          var cs = splitCountries(tbl[k]);
          for (var c = 0; c < cs.length; c++) pushUnique(out, cs[c]);
        }
      }
    }
    var mc = xcConfig(rules).madeIn;
    if (isObj(mc)) for (var b = 0; b < arr(mc.brandCountries).length; b++) pushUnique(out, mc.brandCountries[b]);
    out.sort(function (x, y) { return normName(x) < normName(y) ? -1 : normName(x) > normName(y) ? 1 : 0; });
    return out;
  }

  function modelNames(m) { return [m.name].concat(arr(m.aliases)); }

  function findModel(models, v) {
    var want = String(v);
    for (var i = 0; i < models.length; i++) if (models[i] && models[i].id === want) return models[i];
    var n = normName(want);
    for (var j = 0; j < models.length; j++) {
      if (!models[j]) continue;
      var names = modelNames(models[j]);
      for (var k = 0; k < names.length; k++) if (normName(names[k]) === n) return models[j];
    }
    return null;
  }

  /**
   * Which of OUR models an index entry names. Index entries are design lines
   * with size words ("Dionysus small shoulder bag"), so a model matches when
   * one of its names appears in the entry as whole words, and the LONGEST
   * match wins ("New Jackie" over "Jackie"). Two different models tying for
   * longest is no answer at all — a conflict must positively identify the
   * other model, never guess it.
   */
  function bestModelFor(models, hitName) {
    var h = " " + normName(hitName) + " ";
    var best = null, bestLen = 0, tie = false;
    for (var i = 0; i < models.length; i++) {
      if (!models[i]) continue;
      var names = modelNames(models[i]);
      for (var k = 0; k < names.length; k++) {
        var n = normName(names[k]);
        if (!n || h.indexOf(" " + n + " ") === -1) continue;
        if (n.length > bestLen) { best = models[i]; bestLen = n.length; tie = false; }
        else if (n.length === bestLen && best !== models[i]) tie = true;
      }
    }
    return tie ? null : best;
  }

  /** min/max of the spans at `idx`, for the sentence; finite numbers only. */
  function codeRange(spans, idx) {
    var lo = Infinity, hi = -Infinity;
    for (var i = 0; i < idx.length; i++) {
      var s = spans[idx[i]];
      if (!s) continue;
      if (isFinite(s[0]) && s[0] < lo) lo = s[0];
      if (isFinite(s[1]) && s[1] > hi) hi = s[1];
    }
    return { codeFrom: isFinite(lo) ? lo : null, codeTo: isFinite(hi) ? hi : null };
  }

  function withRange(p, spans, idx) {
    var r = codeRange(spans, idx);
    p.codeFrom = r.codeFrom;
    p.codeTo = r.codeTo;
    return p;
  }

  function pairCompare(def) {
    return def.compare === "pair" || def.compare === "group" ? def.compare : "code";
  }

  /** The declared pair checks, one object or a list; an entry needs an id
   *  when it is in a list, so the item can say which value is which. */
  function pairCodeDefs(cfg) {
    if (isObj(cfg.pairCode)) return [cfg.pairCode];
    var out = [];
    var list = arr(cfg.pairCode);
    for (var i = 0; i < list.length; i++) if (isObj(list[i]) && given(list[i].id)) out.push(list[i]);
    return out;
  }

  /** "120 mm", "120,5" → 120 / 120.5. The first number in the string, or null. */
  function numberIn(v) {
    var m = String(v == null ? "" : v).match(/-?[0-9]+(?:[.,][0-9]+)?/);
    return m ? parseFloat(m[0].replace(",", ".")) : null;
  }

  /**
   * A named capture group of the code, re-read from the ruleset rather than
   * carried on every decode() result: the result shape stays as it was, and
   * the group comes from the SAME era patterns that produced the reading.
   * Only `pattern` eras, matched against `result.input` exactly as decode()
   * matched them; a `rawPattern` era reads a separator-preserving string the
   * result does not keep, so it is skipped rather than half-matched.
   */
  function groupValue(res, rules, name) {
    if (!given(name)) return "";
    var ids = {};
    for (var i = 0; i < arr(res.eras).length; i++) ids[res.eras[i].id] = true;
    var eras = arr(rules && rules.eras);
    for (var e = 0; e < eras.length; e++) {
      var era = eras[e];
      if (!ids[era.id] || era.rawPattern || !era.pattern) continue;
      var re;
      try { re = new RegExp(era.pattern); } catch (x) { continue; }
      var m = re.exec(String(res.input || ""));
      if (!m) continue;
      var g = groupsOf(m, era.groupNames);
      if (given(g[name])) return String(g[name]);
    }
    return "";
  }

  /**
   * What the UI should render, so it never reads the `crossCheck` block
   * itself. A type is OFFERED only when it has something to compare against:
   * an empty or absent model list means no model picker, not an empty one.
   */
  function crossCheckOptions(rules) {
    var cfg = xcConfig(rules);
    var out = { fields: [], models: [], modelIndex: false, countries: [], claimedYear: null,
                pairCode: null, pairCodes: [], features: [] };
    if (isObj(cfg.model)) {
      var ms = arr(cfg.model.models);
      for (var i = 0; i < ms.length; i++) {
        if (isObj(ms[i]) && given(ms[i].id) && given(ms[i].name)) out.models.push({ id: ms[i].id, name: ms[i].name });
      }
      out.models.sort(function (a, b) {
        var x = normName(a.name), y = normName(b.name);
        return x < y ? -1 : x > y ? 1 : (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
      });
      out.modelIndex = cfg.model.modelIndex === true;
      if (out.models.length) out.fields.push("model");
    }
    if (isObj(cfg.madeIn)) {
      out.countries = countriesOf(rules);
      if (out.countries.length) out.fields.push("madeIn");
    }
    if (isObj(cfg.claimedYear)) {
      out.claimedYear = { tolerance: tolOf(cfg.claimedYear.tolerance, 1) };
      out.fields.push("claimedYear");
    }
    // Labels are NOT ruleset data: the UI looks each id up in ui-strings
    // (`crossCheck`), where every other sentence on the card lives. An English
    // label in a ruleset is a translatable leaf, and one untranslated leaf
    // drops the whole localised page below 100% and the build prunes it — 65
    // snippets the first time this was tried. A ruleset MAY still carry a
    // `label`; it is passed through, never required.
    var pds = pairCodeDefs(cfg);
    for (var pc = 0; pc < pds.length; pc++) {
      var pd0 = pds[pc];
      out.pairCodes.push({ id: pd0.id || null, compare: pairCompare(pd0),
                           rule: pd0.rule === "numeric" ? "numeric" : "equal",
                           tolerance: pd0.rule === "numeric" ? tolOf(pd0.tolerance, 0) : null,
                           label: given(pd0.label) ? pd0.label : null });
    }
    if (out.pairCodes.length) { out.pairCode = out.pairCodes[0]; out.fields.push("pairCode"); }
    if (isObj(cfg.feature)) {
      var fs = arr(cfg.feature.features);
      for (var f = 0; f < fs.length; f++) {
        if (isObj(fs[f]) && given(fs[f].id)) out.features.push({ id: fs[f].id, label: given(fs[f].label) ? fs[f].label : null });
      }
      if (out.features.length) out.fields.push("feature");
    }
    return out;
  }

  /**
   * @param {object} result  what decode() returned for the code
   * @param {object} item    { model?, madeIn?, claimedYear?, pairCode?, pairCodeOther?, features? }
   *                         An absent field is not checked.
   * @param {object} rules   the same ruleset
   * @param {object} opts    { now?: Date, modelIndexHit?: string | [name, ...] | {name} }
   *                         `modelIndexHit` is what the CALLER found in a model
   *                         index (Gucci's, fetched lazily by the page). The
   *                         engine never fetches: it stays synchronous, and the
   *                         tested file needs no network.
   * @returns {object} { checks: [{type, status, key, params}], narrowed, overall, fieldsUsed }
   *   `key` is a ui-strings key in the `crossCheck` section; `params` are its
   *   {placeholder} values. `narrowed` is the subset of result.candidates that
   *   fits every given field, or null when nothing was narrowed away.
   */
  function crossCheck(result, item, rules, opts) {
    opts = isObj(opts) ? opts : {};
    item = isObj(item) ? item : {};
    var now = clockOf(opts.now);
    var cfg = xcConfig(rules);
    var res = isObj(result) ? result : { state: STATE.INVALID_FORMAT, candidates: [], eras: [] };
    // OUT_OF_SCOPE is "documented, but not this line": no reading either.
    var noReading = isMiss(res.state) || res.state === STATE.OUT_OF_SCOPE;
    var cands = noReading ? [] : arr(res.candidates);
    var spans = [], dated = [], keep = [];
    for (var i = 0; i < cands.length; i++) {
      spans.push(spanOf(cands[i], now));
      keep.push(true);
      if (spans[i]) dated.push(i);
    }
    var checks = [];
    function add(type, status, key, params) {
      checks.push({ type: type, status: status, key: key, params: params });
    }

    // ── model ────────────────────────────────────────────────────────────────
    // A field the options did not OFFER is not checked: an empty model list
    // must not answer "unknown model" to a reader who could not pick one.
    if (isObj(cfg.model) && arr(cfg.model.models).length && given(item.model)) {
      var models = arr(cfg.model.models);
      var mdl = findModel(models, item.model);
      if (!mdl) {
        add("model", "unclear", "modelUnclearUnknownModel", { model: String(item.model), reason: "unknown-model" });
      } else {
        var decided = false;
        var hit = opts.modelIndexHit;
        var hitName = typeof hit === "string" ? hit : Array.isArray(hit) ? hit[0] : isObj(hit) ? hit.name : null;
        if (cfg.model.modelIndex === true && given(hitName)) {
          // The strongest single check: the code itself names a design. It
          // does not narrow years (a style code carries none), and an index
          // entry that names none of our models falls through to the windows.
          var best = bestModelFor(models, hitName);
          if (best === mdl) {
            add("model", "fits", "modelFitsIndex", { model: mdl.name, indexModel: String(hitName) });
            decided = true;
          } else if (best) {
            add("model", "conflict", "modelConflictIndex",
                { model: mdl.name, indexModel: String(hitName), indexMatch: best.name });
            decided = true;
          }
        }
        if (!decided) {
          var wins = [];
          for (var w = 0; w < arr(mdl.windows).length; w++) {
            var wd = mdl.windows[w];
            // Either end may be open: `to: null` is "still made", `from: null`
            // is "made until", for a model whose launch no source dates.
            if (!isObj(wd) || (typeof wd.from !== "number" && typeof wd.to !== "number")) continue;
            wins.push([typeof wd.from === "number" ? wd.from : -Infinity, typeof wd.to === "number" ? wd.to : now.year, wd]);
          }
          var mp = { model: mdl.name };
          if (wins.length) {
            mp.from = isFinite(wins[0][0]) ? wins[0][0] : null;
            mp.to = typeof wins[wins.length - 1][2].to === "number" ? wins[wins.length - 1][2].to : null;
          }
          if (!wins.length) { mp.reason = "no-windows"; add("model", "unclear", "modelUnclearNoWindows", mp); }
          else if (noReading) { mp.reason = "no-reading"; mp.state = res.state; add("model", "unclear", "modelUnclearNoReading", mp); }
          else if (!dated.length) { mp.reason = "no-date"; mp.state = res.state; add("model", "unclear", "modelUnclearNoDate", mp); }
          else {
            // A candidate PARTIALLY inside a window fits: a Rolex letter
            // spanning 1987 to 1989 and a model launched in 1988 can agree.
            // `tolerance` (default 1) because production precedes launch.
            // ONLY A DOCUMENTED WINDOW CAN ACCUSE. A launch year that rests on
            // one source ("reported") can agree with a code, but outside its
            // window the honest answer is "can't tell", and it never narrows:
            // promoting one reading to THE reading on a single source's say-so
            // is the confident wrong answer this project exists to avoid.
            var documented = mdl.confidence === "documented";
            mp.confidence = documented ? "documented" : (mdl.confidence || null);
            var mt = tolOf(cfg.model.tolerance, 1), mFit = [];
            for (var di = 0; di < dated.length; di++) {
              var ok = false;
              for (var wi = 0; wi < wins.length && !ok; wi++) ok = overlaps(spans[dated[di]], wins[wi], mt);
              if (ok) mFit.push(dated[di]); else if (documented) keep[dated[di]] = false;
            }
            // The years in the sentence are the readings that FIT, so "fits:
            // a Lindy, 2020" never quotes the 1969 reading it just ruled out.
            withRange(mp, spans, mFit.length ? mFit : dated);
            if (mFit.length) add("model", "fits", "modelFits", mp);
            else if (documented) add("model", "conflict", "modelConflict", mp);
            else { mp.reason = "single-sourced"; add("model", "unclear", "modelUnclearReported", mp); }
          }
        }
      }
    }

    // ── madeIn ───────────────────────────────────────────────────────────────
    if (isObj(cfg.madeIn) && countriesOf(rules).length && given(item.madeIn)) {
      var chosen = String(item.madeIn).trim();
      var cp = { country: chosen };
      if (noReading) {
        cp.reason = "no-reading"; cp.state = res.state;
        add("madeIn", "unclear", "madeInUnclearNoReading", cp);
      } else {
        var facts = [];
        for (var fi = 0; fi < cands.length; fi++) if (cands[fi].factory != null) pushUnique(facts, String(cands[fi].factory));
        if (res.factory != null) pushUnique(facts, String(res.factory));
        var set = [];
        for (var fj = 0; fj < facts.length; fj++) {
          var parts = splitCountries(facts[fj]);
          for (var fk = 0; fk < parts.length; fk++) pushUnique(set, parts[fk]);
        }
        cp.source = "factory";
        if (!set.length && isObj(cfg.madeIn) && arr(cfg.madeIn.brandCountries).length) {
          set = arr(cfg.madeIn.brandCountries).slice();
          cp.source = "brand";
        }
        // An UNKNOWN factory is unclear, never a conflict. A competitor marks
        // every country as a mismatch when the factory code is not in its
        // table, which turns its own gap into a verdict on the bag.
        if (!set.length) {
          cp.reason = "unknown-factory";
          add("madeIn", "unclear", "madeInUnclearUnknownFactory", cp);
        } else {
          cp.factory = facts.length ? facts.join(" / ") : set.join(", ");
          cp.countries = set;
          var inSet = false;
          for (var s = 0; s < set.length; s++) if (normName(set[s]) === normName(chosen)) inSet = true;
          for (var fc = 0; fc < cands.length; fc++) {
            if (cands[fc].factory == null) continue;
            var cs = splitCountries(cands[fc].factory), mine = false;
            for (var cx = 0; cx < cs.length; cx++) if (normName(cs[cx]) === normName(chosen)) mine = true;
            if (!mine) keep[fc] = false;
          }
          add("madeIn", inSet ? "fits" : "conflict", inSet ? "madeInFits" : "madeInConflict", cp);
        }
      }
    }

    // ── claimedYear ──────────────────────────────────────────────────────────
    var claimed = typeof item.claimedYear === "number" ? item.claimedYear : parseInt(String(item.claimedYear), 10);
    if (isObj(cfg.claimedYear) && given(item.claimedYear) && !isNaN(claimed)) {
      var yp = { claimedYear: claimed };
      var ey = endedYear(rules);
      if (claimed > now.year) {
        // Not a mismatch between the code and the item; the year typed has not
        // happened. Unclear, so it can never drive the conflict CTA.
        yp.reason = "claimed-future";
        add("claimedYear", "unclear", "claimedYearUnclearFuture", yp);
      } else if (noReading) {
        yp.reason = "no-reading"; yp.state = res.state;
        add("claimedYear", "unclear", "claimedYearUnclearNoReading", yp);
      } else if (!dated.length) {
        yp.reason = "no-date"; yp.state = res.state;
        add("claimedYear", "unclear", "claimedYearUnclearNoDate", yp);
      } else if (cfg.claimedYear.codeEnded === true && ey !== null && claimed > ey) {
        // "LV stopped date codes in March 2021; a 2023 bag should carry a chip
        // instead." Opt-in per ruleset: on the watch rulesets `codeEndedAt`
        // marks where the PUBLISHED RECORD ends, not where the brand stopped
        // numbering, and a 2023 IWC is not a contradiction of anything.
        yp.endedAt = rules.codeEndedAt.date;
        yp.endedYear = ey;
        yp.replacedBy = rules.codeEndedAt.replacedBy || null;
        if (given(cfg.claimedYear.codeEndedException)) yp.exception = cfg.claimedYear.codeEndedException;
        for (var ke = 0; ke < dated.length; ke++) keep[dated[ke]] = false;
        withRange(yp, spans, dated);
        add("claimedYear", "conflict", "claimedYearConflictCodeEnded", yp);
      } else {
        var yt = tolOf(cfg.claimedYear.tolerance, 1), yFit = [];
        for (var yi = 0; yi < dated.length; yi++) {
          if (overlaps(spans[dated[yi]], [claimed, claimed], yt)) yFit.push(dated[yi]);
          else keep[dated[yi]] = false;
        }
        withRange(yp, spans, yFit.length ? yFit : dated);
        add("claimedYear", yFit.length ? "fits" : "conflict", yFit.length ? "claimedYearFits" : "claimedYearConflict", yp);
      }
    }

    // ── pairCode: two values that must agree ────────────────────────────────
    // Not a period check, so it runs on any state: two numbers that should be
    // the same are either the same or not, whether or not we can date them.
    //
    // One check type, three sources for the second value, two comparisons:
    //   compare "code"  — the reader's value against the main code (Chanel's
    //                     card against its sticker, where a source says so)
    //   compare "pair"  — two values the reader types, neither of them the code
    //                     (Balenciaga's front and back style numbers)
    //   compare "group" — against a NAMED CAPTURE GROUP of the code as decoded
    //                     (a heel height read out of a style name, against the
    //                     heel the reader measured)
    //   rule "equal"    — normalised string equality, the default
    //   rule "numeric"  — |a − b| ≤ tolerance, for a measured value
    // `pairCode` may be one object or a list of them; with a list, the item
    // gives `pairCode: {id: value}` and `pairCodeOther: {id: value}`.
    var pairDefs = pairCodeDefs(cfg);
    for (var pd = 0; pd < pairDefs.length; pd++) {
      var pdef = pairDefs[pd];
      var listMode = Array.isArray(cfg.pairCode);
      var pv = listMode ? (isObj(item.pairCode) ? item.pairCode[pdef.id] : null) : item.pairCode;
      if (!given(pv)) continue;
      var other = listMode ? (isObj(item.pairCodeOther) ? item.pairCodeOther[pdef.id] : null) : item.pairCodeOther;
      var pmode = pairCompare(pdef), numeric = pdef.rule === "numeric";
      var pa = "";
      if (pmode === "pair") pa = given(other) ? String(other) : "";
      else if (pmode === "group") pa = noReading ? "" : groupValue(res, rules, pdef.group);
      else pa = String(res.input || "");
      var pb = String(pv);
      if (!numeric) { pb = normalize(pb, rules); pa = pmode === "code" ? pa : normalize(pa, rules); }
      var pp = { pairId: pdef.id || null, compare: pmode, rule: numeric ? "numeric" : "equal",
                 pairCode: pb, code: pa };
      if (given(pdef.label)) pp.label = pdef.label;
      if (numeric) pp.tolerance = tolOf(pdef.tolerance, 0);
      var na = numeric ? numberIn(pa) : null, nb = numeric ? numberIn(pb) : null;
      if (!pb || !pa || (numeric && (na === null || nb === null))) {
        pp.reason = pmode === "pair" ? "needs-both" : "no-code";
        add("pairCode", "unclear", pmode === "pair" ? "pairCodeUnclearNeedsBoth" : "pairCodeUnclearNoCode", pp);
        continue;
      }
      var same = numeric ? Math.abs(na - nb) <= pp.tolerance : pa === pb;
      if (numeric) { pp.code = na; pp.pairCode = nb; }
      add("pairCode", same ? "fits" : "conflict", same ? "pairCodeFits" : "pairCodeConflict", pp);
    }

    // ── feature ──────────────────────────────────────────────────────────────
    // Only a TICKED feature is evidence. An unticked chip box is not "has no
    // chip" — readers skip boxes — so false and absent are both "not given".
    if (isObj(cfg.feature) && isObj(item.features)) {
      var defs = arr(cfg.feature.features);
      for (var fd = 0; fd < defs.length; fd++) {
        var def = defs[fd];
        if (!isObj(def) || !given(def.id) || item.features[def.id] !== true) continue;
        var hasYears = typeof def.impliesYearFrom === "number" || typeof def.impliesYearTo === "number";
        var inc = arr(def.impliesEras), exc = arr(def.excludesEras);
        var hasEras = inc.length > 0 || exc.length > 0;
        var fDocumented = def.confidence === "documented";
        var fp = { featureId: def.id, feature: given(def.label) ? def.label : null,
                   confidence: fDocumented ? "documented" : (def.confidence || null),
                   impliedFrom: typeof def.impliesYearFrom === "number" ? def.impliesYearFrom : null,
                   impliedTo: typeof def.impliesYearTo === "number" ? def.impliesYearTo : null };
        var eraOk = function (id) {
          if (inc.length && inc.indexOf(id) === -1) return false;
          if (exc.length && exc.indexOf(id) !== -1) return false;
          return true;
        };
        if (noReading) {
          fp.reason = "no-reading"; fp.state = res.state;
          add("feature", "unclear", "featureUnclearNoReading", fp);
          continue;
        }
        if (!hasYears && !hasEras) continue;
        // Default tolerance 1, as for the year checks: changeovers were
        // progressive (LV fitted chips through 2021, Chanel's last sticker
        // series ran into 2022), so a 2020 code beside a chip is a transition
        // piece, not a contradiction. 2019 and a chip is the conflict.
        var ft = tolOf(def.tolerance, 1);
        var fwin = [fp.impliedFrom === null ? -Infinity : fp.impliedFrom, fp.impliedTo === null ? now.year : fp.impliedTo];
        var anyYes = false, anyNo = false, fYes = [];
        if (!cands.length) {
          // A no-date reading (FORMAT_OK_NO_DATE) still has ERAS, so an
          // era-implying feature can be checked; a year-implying one cannot.
          if (hasEras && !hasYears) {
            var rex = arr(res.eras);
            for (var re = 0; re < rex.length; re++) { if (eraOk(rex[re].id)) anyYes = true; else anyNo = true; }
          }
        } else {
          for (var ci = 0; ci < cands.length; ci++) {
            var verdict = true;
            if (hasYears) verdict = spans[ci] ? overlaps(spans[ci], fwin, ft) : null;
            if (hasEras && verdict !== false) verdict = eraOk(cands[ci].era) ? verdict : false;
            if (verdict === true) { anyYes = true; if (spans[ci]) fYes.push(ci); }
            else if (verdict === false) { anyNo = true; if (fDocumented) keep[ci] = false; }
          }
        }
        withRange(fp, spans, fYes.length ? fYes : dated);
        // Same rule as models: only a documented feature can accuse or narrow.
        if (anyYes) add("feature", "fits", "featureFits", fp);
        else if (anyNo && fDocumented) add("feature", "conflict", "featureConflict", fp);
        else if (anyNo) { fp.reason = "single-sourced"; add("feature", "unclear", "featureUnclearReported", fp); }
        else { fp.reason = "no-date"; fp.state = res.state; add("feature", "unclear", "featureUnclearNoDate", fp); }
      }
    }

    var overall = null;
    for (var oc = 0; oc < checks.length; oc++) {
      if (checks[oc].status === "conflict") { overall = "conflict"; break; }
      if (checks[oc].status === "fits") overall = "fits";
      else if (overall === null) overall = "unclear";
    }
    // Narrowed only when some, not all, survive — and never on a conflict: the
    // spec's "consistent with every given field" has no members when one field
    // contradicts the code outright.
    var narrowed = null;
    if (overall !== "conflict" && cands.length > 1) {
      var surv = [];
      for (var ni = 0; ni < cands.length; ni++) if (keep[ni]) surv.push(cands[ni]);
      if (surv.length && surv.length < cands.length) narrowed = surv;
    }
    var used = [];
    for (var u = 0; u < checks.length; u++) if (used.indexOf(checks[u].type) === -1) used.push(checks[u].type);
    used.sort();
    return { checks: checks, narrowed: narrowed, overall: overall, fieldsUsed: used.join(",") };
  }

  return {
    STATE: STATE,
    OPS: Object.keys(OPS),
    decode: decode,
    eraTable: eraTable,
    isMiss: isMiss,
    crossCheck: crossCheck,
    crossCheckOptions: crossCheckOptions,
    CROSS_CHECK_TYPES: CROSS_CHECK_TYPES,
    // Exported because the UI needs it to pick a message, and because a helper
    // the browser depends on belongs in the file the tests import.
    hasNonLatinLetters: hasNonLatinLetters,
    _internal: { normalize: normalize, twoDigitYear: twoDigitYear, foldInput: foldInput, OPS: OPS,
                 clockOf: clockOf, countriesOf: countriesOf }
  };
});
